import { useEffect, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Wrapper from '../common/Wrapper'
import DataSet from 'common-ui/components/DataSet'
import GetApp from '@mui/icons-material/GetApp'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'

import { useBillingApi } from '../../api/billing/billing-context'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { endOfDayUTC, endOfMonthUTC, startOfDayUTC, startOfMonthUTC } from 'common/api/v1/helpers'
import { BillingSummary } from '../common/BillingSummary'

import { Role, type BillingReport, type Customer, type Installation } from 'common-billing-server/types'
import { formatDate } from '../../format-date'
import { DateRangePicker } from '../common/DateRangePicker'
import { InstallationRtuAllocationComponent } from './InstallationRtuAllocation'
import { InstallationAiEulaComponent } from './InstallationAiEula'
import { RoleBasedComponent, RoleBasedLink } from '../common/RoleBasedLink'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import { makeArchiveInstallationOperation, makeRestoreInstallationOperation } from './InstallationActions'
import { makeEditCustomerPath } from '../../routes/path'

export const InstallationDetailsPage = () => {
    const billingApi = useBillingApi()
    const location = useLocation()
    const urlParams = useParams()

    const customerId: string | undefined = urlParams.customerId
    const [customer, setCustomer] = useState<Customer | undefined>()

    const installationId: string | undefined = urlParams.installationId
    const [billingReport, setBillingReport] = useState<BillingReport | undefined>()
    const [installation, setInstallation] = useState<Installation>()
    const host = installation?.host
    const [isChangingArchiveState, setIsChangingArchiveState] = useState(false)
    const showConfirmation = useConfirmationDialog()

    const [isLoading, setIsLoading] = useState(false)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const [searchParams, setSearchParams] = useSearchParams()
    const startDateUrlParam = searchParams.get('startDate')
    const endDateUrlParam = searchParams.get('endDate')

    const [activeTab, setActiveTab] = useState(0)

    const setDate = (key: 'startDate' | 'endDate', value: Date | null) => {
        if (!value || !value.getTime()) return
        const hasBillingReport = !!billingReport
        const currentValue = searchParams.get(key)
        const newValue = value.toISOString()
        const hasSelectedSameValue =
            currentValue && formatDate(new Date(currentValue)) === formatDate(new Date(newValue))
        if (hasBillingReport && hasSelectedSameValue) {
            return
        }
        searchParams.set(key, newValue)
        setSearchParams(searchParams, { replace: true })
    }

    useEffect(() => {
        async function fetchCustomer() {
            const operationId = 'fetch-installation-customer'
            if (!customerId) return
            removeFailedOperation(operationId)
            setCustomer(undefined)
            setIsLoading(true)
            try {
                const customers = await billingApi.listCustomers({ filter: { customerId } })
                const customer = customers.items[0]
                if (!customer) {
                    throw new Error(`No customer found with id '${customerId}'`)
                }
                setCustomer(customer)

                let endDate = endDateUrlParam ? new Date(endDateUrlParam) : undefined
                const isTooEarlyEndDate = endDate && endDate.getTime() < customer.billingStartsAt.getTime()
                if (!endDate || isTooEarlyEndDate) {
                    // Don't allow end date to be earlier than billing start time
                    endDate = new Date(
                        Math.max(customer.billingStartsAt.getTime(), endOfMonthUTC(new Date()).getTime())
                    )
                    setDate('endDate', endOfDayUTC(endDate))
                }

                let startDate = startDateUrlParam ? new Date(startDateUrlParam) : undefined
                const isTooEarlyStartDate = startDate && startDate.getTime() < customer.billingStartsAt.getTime()
                const isTooLateStartDate = startDate && startDate.getTime() > endDate.getTime()
                if (!startDate || isTooEarlyStartDate || isTooLateStartDate) {
                    // Start date must be between billing start time and end date
                    startDate = new Date(
                        Math.max(customer.billingStartsAt.getTime(), startOfMonthUTC(new Date()).getTime())
                    )
                    setDate('startDate', startOfDayUTC(startDate))
                }
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching customer',
                    error,
                    retryFn: () => void fetchCustomer(),
                })
            }
        }
        void fetchCustomer()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!installationId) return
        let cancelled = false
        const operationId = 'fetch-installation'
        setInstallation(undefined)
        removeFailedOperation(operationId)
        void (async () => {
            try {
                const { items } = await billingApi.listInstallations({
                    filter: { installationId, excludeDeleted: false },
                })
                if (!items[0]) throw new Error(`No installation found with id '${installationId}'`)
                if (!cancelled) setInstallation(items[0])
            } catch (error) {
                if (!cancelled) addFailedOperation({ id: operationId, message: 'Failed fetching installation', error })
            }
        })()
        return () => {
            cancelled = true
        }
    }, [billingApi, installationId, addFailedOperation, removeFailedOperation])

    useEffect(() => {
        async function fetchBillingReport() {
            const operationId = 'fetch-billing-report'
            if (!customer) return
            if (!installationId) return
            if (!startDateUrlParam) return
            if (!endDateUrlParam) return

            removeFailedOperation(operationId)
            setBillingReport(undefined)
            setIsLoading(true)
            try {
                const billingReport = await billingApi.getInstallationBillingReport(installationId, {
                    startDate: new Date(startDateUrlParam).toISOString(),
                    endDate: new Date(endDateUrlParam).toISOString(),
                })
                setBillingReport(billingReport)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching billing report',
                    error,
                    retryFn: () => void fetchBillingReport(),
                })
            }
            setIsLoading(false)
        }

        void fetchBillingReport()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, installationId, startDateUrlParam, endDateUrlParam])

    function makeBillingReportPdfUrl(installationId: string, startDate: string, endDate: string) {
        const searchParams = new URLSearchParams()
        searchParams.set('startDate', startDate)
        searchParams.set('endDate', endDate)
        searchParams.set('format', 'pdf')
        return `/api/installation/${installationId}/billing/report?${searchParams.toString()}`
    }

    const changeArchiveState = () => {
        if (!installation) return
        const operation = installation.deleted
            ? makeRestoreInstallationOperation(installation.id)
            : makeArchiveInstallationOperation(installation.id)
        showConfirmation(
            () => {
                void (async () => {
                    setIsChangingArchiveState(true)
                    removeFailedOperation(operation.id)
                    try {
                        await operation.exec(billingApi)
                        setInstallation((current) =>
                            current?.id === installation.id ? { ...current, deleted: !installation.deleted } : current
                        )
                    } catch (error) {
                        addFailedOperation({ id: operation.id, message: `${operation.name} failed`, error })
                    } finally {
                        setIsChangingArchiveState(false)
                    }
                })()
            },
            installation.deleted
                ? `Restore installation ${installation.host}?`
                : `Archive installation ${installation.host}? Its billing history will be preserved.`,
            {
                ok: {
                    text: installation.deleted ? 'Restore' : 'Archive',
                    startIcon: installation.deleted ? <UnarchiveIcon /> : <ArchiveIcon />,
                },
            }
        )
    }

    return (
        <Wrapper
            title={host}
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {activeTab === 0 && installationId && startDateUrlParam && endDateUrlParam && (
                        <Link
                            underline="none"
                            href={makeBillingReportPdfUrl(installationId, startDateUrlParam, endDateUrlParam)}
                        >
                            <Button variant="outlined" startIcon={<GetApp />}>
                                Download
                            </Button>
                        </Link>
                    )}
                    {installation && installation.id === installationId && (
                        <RoleBasedComponent
                            minimumAllowedRole={
                                !installation.deleted && installation.lastReportAt ? Role.admin : Role.licenseAdmin
                            }
                        >
                            <Button
                                variant="outlined"
                                disabled={isChangingArchiveState}
                                startIcon={installation.deleted ? <UnarchiveIcon /> : <ArchiveIcon />}
                                onClick={changeArchiveState}
                            >
                                {installation.deleted ? 'Restore installation' : 'Archive installation'}
                            </Button>
                        </RoleBasedComponent>
                    )}
                </Box>
            }
        >
            {installation?.deleted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This installation is archived. Its billing history is preserved.
                </Alert>
            )}
            {customer && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <DataSet
                        values={{
                            ...(host && { Host: host }),
                            Customer: (
                                <RoleBasedLink
                                    minimumAllowedRole={Role.admin}
                                    to={makeEditCustomerPath(customer.id)}
                                    state={{ from: location }}
                                >
                                    {customer.name}
                                </RoleBasedLink>
                            ),
                            'Customer number': customer.customerNumber,
                            'Billing type': customer.billingType,
                        }}
                    />
                </Paper>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                    <Tab label="Legacy Billing" />
                    <Tab label="RTU Allocation" />
                    <Tab label="AI Assistant Agreement" />
                </Tabs>
            </Box>

            {activeTab === 0 && startDateUrlParam && endDateUrlParam && (
                <Paper sx={{ mt: 2 }}>
                    <div
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', padding: '20px' }}
                    >
                        <DateRangePicker
                            minDate={customer?.billingStartsAt}
                            startDateString={startDateUrlParam}
                            endDateString={endDateUrlParam}
                            onChange={setDate}
                        />
                        {isLoading && <CircularProgress style={{ marginLeft: 12, padding: '8px' }} />}
                    </div>
                    {billingReport && <BillingSummary billing={billingReport} />}
                </Paper>
            )}

            {activeTab === 1 && installationId && (
                <Box sx={{ mt: 2 }}>
                    <InstallationRtuAllocationComponent
                        installationId={installationId}
                        installationName={billingReport?.periods[0]?.host || 'Installation'}
                        readOnly={installation?.id !== installationId || installation.deleted || isChangingArchiveState}
                    />
                </Box>
            )}

            {activeTab === 2 && installationId && (
                <Box sx={{ mt: 2 }}>
                    <InstallationAiEulaComponent installationId={installationId} />
                </Box>
            )}

            {errorBanner}
        </Wrapper>
    )
}
